import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/context";
import { Btn, Logo } from "../components/Shell";
import { ShieldCheck, CheckCircle2, QrCode, CreditCard } from "lucide-react";
import { useTheme } from "../lib/theme";

export function AuthCallback() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const done = useRef(false);
  const [mfa, setMfa] = useState(null);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(loc.hash.slice(1)).get("session_id");
    api.post("/auth/google/session", { session_id: sid }).then((r) => {
      window.history.replaceState(null, "", "/app");
      if (r.data.mfa_required) return setMfa(r.data);
      setUser(r.data.user);
      nav("/app", { replace: true });
    }).catch(() => nav("/login", { replace: true }));
  }, [loc.hash, nav, setUser]);
  if (mfa) return <Login presetMfa={mfa} />;
  return <div className="p-10 text-center text-sm text-slate-500">Signing you in…</div>;
}

export default function Login({ presetMfa }) {
  useTheme("none");
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ email: "", password: "", name: "" });
  const [mfa, setMfa] = useState(presetMfa || null);
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const r = await api.post(mode === "login" ? "/auth/login" : "/auth/register", f);
      if (r.data.mfa_required) setMfa(r.data);
      else { setUser(r.data.user); nav("/app"); }
    } catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  };
  const verify = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const r = await api.post("/auth/mfa/verify", { mfa_token: mfa.mfa_token, code });
      setUser(r.data.user);
      nav("/app");
    } catch (x) { setErr(errMsg(x)); }
  };
  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  const inp = "min-h-[48px] w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm shadow-sm transition-all focus:border-violet-500 focus:outline-none focus:ring-4 focus:ring-violet-500/15";
  return (
    <div className="relative grid min-h-screen overflow-hidden bg-[#0E0B1F] lg:grid-cols-[1.15fr_1fr]">
      <div className="relative hidden flex-col justify-between p-12 text-white lg:flex">
        <div className="pointer-events-none absolute -left-24 top-1/3 h-[30rem] w-[30rem] rounded-full bg-violet-600/40 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-40 right-0 h-[26rem] w-[26rem] rounded-full bg-[#FF5A5F]/40 blur-3xl" />
        <div className="relative"><Logo className="text-3xl" /></div>
        <div className="relative">
          <h1 className="text-6xl font-extrabold leading-[1.02]">The Intelligence Behind<br /><span className="sx-text">Every Transaction.</span></h1>
          <p className="mt-6 max-w-md text-base text-slate-300">Sign in to the SwitchX operations console or the account onboarding workspace. This is a demo environment and no real money moves.</p>
          <div className="mt-10 flex flex-wrap gap-4">
            {[["Account opened", "Savings & Current", CheckCircle2, "bg-[#C6F432] text-[#0E0B1F]"], ["QR ready", "Merchant QR", QrCode, "sx-grad text-white"], ["PG · POS", "one profile", CreditCard, "bg-white text-[#0E0B1F]"]].map(([a, b, I, c], i) => (
              <div key={a} className={`floaty flex items-center gap-3 rounded-2xl px-4 py-3 shadow-2xl ${c}`} style={{ animationDelay: `${i * 0.8}s` }}><I className="h-5 w-5" /><div><div className="text-sm font-bold">{a}</div><div className="text-xs opacity-70">{b}</div></div></div>
            ))}
          </div>
        </div>
        <p className="relative text-xs text-slate-500">Demo environment · sandbox providers are clearly labelled and never shown as real approvals.</p>
      </div>
      <div className="relative flex flex-col items-center justify-center gap-6 p-6">
        <div className="lg:hidden"><Logo className="text-3xl" /></div>
        <div className="w-full max-w-md space-y-6 rounded-[2rem] bg-white p-8 shadow-2xl shadow-violet-900/40 sm:p-10">
          <span className="inline-flex rounded-full bg-amber-100 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-amber-800" data-testid="login-sandbox-tag">Sandbox demo</span>
          {mfa ? (
            <form onSubmit={verify} className="space-y-4" data-testid="mfa-form">
              <div className="flex items-center gap-2"><ShieldCheck className="h-6 w-6" /><h2 className="text-2xl font-bold">Two-step verification</h2></div>
              {mfa.setup && <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs" data-testid="mfa-setup">Add this key to your authenticator app: <code className="break-all font-mono font-bold">{mfa.setup.secret}</code></div>}
              <label htmlFor="mfa-code" className="text-sm font-medium">6-digit code from your authenticator app</label>
              <input id="mfa-code" data-testid="mfa-code-input" inputMode="numeric" autoComplete="one-time-code" className={inp} value={code} onChange={(e) => setCode(e.target.value)} />
              {err && <p role="alert" className="text-sm text-red-600" data-testid="auth-error">{err}</p>}
              <Btn className="w-full" data-testid="mfa-verify-btn">Verify</Btn>
            </form>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
              <h2 className="text-3xl font-extrabold">{mode === "login" ? "Welcome back" : "Create applicant account"}</h2>
              <p className="-mt-2 text-sm text-slate-500">{mode === "login" ? "Sign in to continue your onboarding" : "It takes less than a minute"}</p>
              {mode === "register" && <div><label htmlFor="n" className="text-sm font-medium">Full name</label><input id="n" data-testid="register-name-input" className={inp} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required /></div>}
              <div><label htmlFor="e" className="text-sm font-medium">Email</label><input id="e" type="email" data-testid="login-email-input" className={inp} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required /></div>
              <div><label htmlFor="p" className="text-sm font-medium">Password</label><input id="p" type="password" data-testid="login-password-input" className={inp} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required /></div>
              {err && <p role="alert" className="text-sm text-red-600" data-testid="auth-error">{err}</p>}
              <Btn className="w-full" disabled={busy} data-testid="login-submit-btn">{busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}</Btn>
              <Btn type="button" variant="outline" className="w-full" onClick={google} data-testid="google-login-btn">Continue with Google</Btn>
              <button type="button" className="w-full text-sm text-slate-600 underline" data-testid="toggle-auth-mode" onClick={() => setMode(mode === "login" ? "register" : "login")}>
                {mode === "login" ? "New applicant? Create an account" : "Already registered? Sign in"}
              </button>
              <Link to="/forgot-password" className="block w-full text-center text-sm text-slate-600 underline" data-testid="forgot-link">Forgot password?</Link>
              <p className="text-xs text-slate-500">Staff accounts require an authenticator code. We never ask for bank passwords, OTPs, card PINs or UPI PINs.</p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
